// @flow
import styled from 'styled-components';
import { H1 } from '@finect/tabular-core/Headings';
import { ButtonLined } from '@finect/tabular-core/ButtonLined';
import { Button } from '@finect/tabular-core/Button';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { getFont } from '@finect/tabular-utils/getFont';

export const Inner = styled.div`
  display: flex;
  flex: auto;
  padding: 16px 0 0;

  ${maxMediaQueries.phablet`
    flex-flow: column;
    justify-content: center;
    align-items: center;
  `}
`;

export const IconBlock = styled.div`
  ${maxMediaQueries.phablet`
    > span {
      margin: 0;
    }
  `}
`;

export const InfoBlock = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;

  ${maxMediaQueries.phablet`
    align-items: center;
    text-align: center;
  `}
`;

/* Fila superior: nombre + badge a la izquierda, acciones (mensaje / seguir) arriba a la derecha */
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  ${maxMediaQueries.phablet`
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  `}
`;

export const Name = styled(H1)`
  margin-right: 12px;

  &:not(:last-child) {
    padding-bottom: 0;
  }

  ${maxMediaQueries.phablet`
    ${getFont(3, 'compact')}
  `}
`;

export const NameBlock = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;

  ${maxMediaQueries.phablet`
    padding-top: 16px;
    flex-flow: column;
  `}

  /* Badge "Experto financiero" (AdviserBadge del paquete): más presencia y alineado al nombre */
  > span {
    align-self: center;
    padding: 4px 12px;
    border-radius: 999px;
    font-weight: 600;
    line-height: 1;
  }
`;

export const HeadlineBlock = styled.div``;

export const ProfileActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  button,
  a:not(:first-of-type) {
    margin-left: 6px;
  }
`;

export const BadgesContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 16px 0;

  /*
   * Mejoras sobre el OrganizationBadge del paquete (solo se usa en este perfil): chips de
   * marca tipo "outline". Colores 100% del theme; geometría alineada con el componente Chip
   * de Finect (alto 30px, radio 16px). El prop \`type\` llega al DOM como atributo, así que
   * distinguimos grupo de certificados por CSS.
   */
  > span[type] {
    height: auto;
    min-height: 30px;
    align-items: center;
    margin: 0;
    padding: 3px 12px 3px 4px;
    border-radius: 16px;
    background-color: ${({ theme }) => theme.colors.terciaryLL};
    border: 1px solid;
    box-shadow: none;
  }

  /* Grupo / organización: dorado de marca (theme.badgeColor = accent3) */
  > span[type='organization'] {
    border-color: ${({ theme }) => theme.colors.accent3};
    color: ${({ theme }) => theme.colors.accent3D};
  }

  /* Certificados: neutro de marca */
  > span[type='certificate'] {
    border-color: ${({ theme }) => theme.colors.ui};
    color: ${({ theme }) => theme.colors.fontL};
  }

  /* Logo / imagen: tile circular (encaja con el borde redondeado del chip y los avatares de Finect) */
  > span[type] > div {
    display: flex;
    flex: 0 0 auto;
    align-self: center;
    width: 24px;
    height: 24px;
    margin-right: 9px;
    border-radius: 50%;
    overflow: hidden;
  }

  > span[type] > div img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Icono de respaldo (Briefcase, cuando no hay logo): tile circular con fondo claro.
     box-sizing border-box para que el padding no agrande el svg a 34px (sin reset global de
     border-box el icono se salía del tile y se recortaba por abajo). */
  > span[type] > svg {
    box-sizing: border-box;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    margin: 0 9px 0 0;
    padding: 5px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.uiLL};
    overflow: visible;
  }

  /* Texto del badge */
  > span[type] > span {
    padding: 0;
  }

  /*
   * Móvil: chips apilados a todo el ancho, contenido alineado a la izquierda y texto que
   * fluye en varias líneas (el del paquete se truncaba con "…" y escondía el certificado).
   */
  ${maxMediaQueries.phablet`
    align-self: stretch;
    flex-direction: column;
    align-items: stretch;

    > span[type] {
      width: 100%;
      justify-content: flex-start;
      min-height: 0;
      padding: 7px 14px 7px 7px;
      text-align: left;
    }

    > span[type] > span {
      display: block;
      -webkit-line-clamp: unset;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      line-height: 1.25;
    }
  `}
`;

// TODO: Todos estos botones tienen que estar estandarizados en components

export const ConfigButton = styled(Button)`
  display: flex;
  padding: 6px 8px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.uiD};

  &:not([disabled]):hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.uiDD};
  }
`;

export const WriteButton = styled(ButtonLined)`
  display: flex;
  align-items: center;
  padding: 6px 12px;

  &:not([disabled]):hover {
    border-color: ${({ theme, secondary }) => (secondary ? theme.colors.secondaryM : theme.colors.primaryM)};
    background-color: ${({ theme, secondary }) => (secondary ? theme.colors.secondaryM : theme.colors.primaryM)};
  }

  svg {
    margin-right: 6px;
  }
`;

export const MessageButton = styled(WriteButton)`
  border: 0;

  &:not([disabled]):hover {
    background-color: transparent;
    color: ${({ theme, secondary }) => (secondary ? theme.colors.secondaryD : theme.colors.primaryD)};
  }
`;

export const FollowButton = styled(WriteButton)`
  background-color: ${({ theme, followed }) => (followed ? theme.colors.primary : theme.colors.secondary)};
`;
